import React, { useContext, useRef, useState } from 'react';
import { Button, Grid, Stack } from '@mui/material';
import { LanguageContext } from '../../../../../../libs/Context';
import UploadBox, { UploadBoxProps } from '../../../../../../components/Common/UploadBox';
import Theming from './Components/Theming';
import { useSelector } from 'react-redux';
import { dataURLtoFile, generateRandomUUID, imageTypeConvert, newUploadFiles, s3DeleteNew, s3FileUpload } from '../../../../../../libs/HelperContent';
import { toast } from 'react-toastify';
import Loading from '../../../../../../components/Common/Loaders/FullWidthLoader/Loading';
import PreviewCropImg from '../../../../../../components/Common/PreviewCropImg';
import { updateMeeting } from '../../../../../actions/meeting_actions';
import ReactDOM from 'react-dom';
import BrandingPreview from './Components/BrandingPreview';

type FileInputRefs = {
	[key in 'logo' | 'background' | 'introVideo']: React.RefObject<HTMLInputElement>;
};

interface LocalUploadBoxProps extends Omit<UploadBoxProps, 'handleClick' | 'handleChange' | 'handleDelete'> {
	onUploadClick: () => void;
	onUploadChange: UploadBoxProps['handleChange'];
	onDelete: UploadBoxProps['handleDelete']; // Add this prop
}

const imgTypes = [ 'image/jpeg', 'image/jpg', 'image/png' ];

const Branding = (props) =>
{
	const { language } = useContext(LanguageContext);
	const sizeErrorObj = {
		logo       : language?.logoSizeLimit,
		background : language?.backgroundSizeLimit,
		introVideo : language?.videoSizeLimit
	};

	const formatErrorObj = {
		logo       : language?.allowedImageFormats,
		background : language?.allowedImageFormatsWithoutSvg,
		introVideo : language?.allowedVideoFormat
	};
	const { form, setForm, handleBackCallBack,
		handleSkipFinish, handleNextCallBack, handleDeletePop } = props;
	const userData = useSelector((state:any) => state.mysession.session);
	const [ cropper, setCropper ] = useState(null);
	const [ loading, setLoading ] = useState(false);
	const [ initialState, setInitialState ] = useState<any>({
		isLogoFiled      : false,
		isLoading        : false,
		showImageCropper : false,
		src              : null,
		file             : null,
		fileName         : '',
		selectedPic      : ''
	});
	const [ colorStates, setColorStates ] = useState({
		primaryColor   : form?.webinarTheme?.primary || ' #042531',
		secondaryColor : form?.webinarTheme?.navBar || '#043040'
	});
	const [ preview, setPreview ] = useState(false);

	const fileInputRefs: FileInputRefs = {
		logo       : useRef<HTMLInputElement>(null),
		background : useRef<HTMLInputElement>(null),
		introVideo : useRef<HTMLInputElement>(null)
	};

	const [ files, setFiles ] = useState<any>({
		logo       : form?.webinarLogo?.[0] ?? null,
		background : form?.webinarBackground?.[0] ?? null,
		introVideo : form?.webinarIntroVideo?.key ? form?.webinarIntroVideo : null
	});

	const handleFileClick = (type: keyof FileInputRefs) => () =>
	{
		fileInputRefs[type].current?.click();
	};

	const onSelectFile = (fileData) =>
	{
		const file = fileData.target.files[0];

		const isImageTypeValid = imgTypes.includes(file.type);

		if (!isImageTypeValid)
		{
			toast.error(language?.onlyAllowedFormats, {
				position  : toast.POSITION.TOP_RIGHT,
				className : 'toast-message'
			});

			return;
		}
		const fiveMB = (1024 * 1024) * 5;

		if (file?.size > fiveMB)
		{
			toast.error(language?.logoSizeLimit, {
				position  : toast.POSITION.TOP_RIGHT,
				className : 'toast-message'
			});

			return;
		}

		const reader = new FileReader();

		reader.onload = () =>
		{
			setInitialState((prevState) => ({
				...prevState,
				src              : reader.result,
				showImageCropper : true,
				file,
				fileName         : file.name,
				selectedPic      : 'logo'
			}));
		};
		reader.readAsDataURL(file);
	};

	const handleImageCrop = async () =>
	{
		setInitialState((prev) => ({ ...prev, isLoading: true }));
		const fileData = dataURLtoFile(cropper, initialState.fileName);

		imageTypeConvert(fileData, async (convertedImgData) =>
		{
			if (!convertedImgData)
			{
				toast.error(language?.conversionError);
				setInitialState((prev) => ({ ...prev, isLoading: false }));

				return;
			}
			try
			{
				const folderPath = `${userData?.companyId}/webinar`;
				const upload = await newUploadFiles([ convertedImgData ], folderPath);

				setFiles((prev) => ({
					...prev,
					logo : { ...upload[0] }
				}));
				setInitialState({
					isLogoFiled      : false,
					isLoading        : false,
					showImageCropper : false,
					src              : null,
					file             : null
				});
			}
			catch (err)
			{
				// eslint-disable-next-line no-console
				console.error(err);
				setInitialState((prev) => ({ ...prev, isLogoFiled: false, isLoading: false }));
			}
		});
	};
	const throwSizeEr = (type) =>
	{
		return toast.error(`${sizeErrorObj[type]}`, {
			position : toast.POSITION.TOP_RIGHT
		});
	};
	const throwFromErr = (type) =>
	{
		return toast.error(`${formatErrorObj[type]}`, {
			position : toast.POSITION.TOP_RIGHT
		});
	};

	const uploadFile = async (file, type) =>
	{
		setLoading(true);
		const folderPath = `${userData?.userId}/webinar`;

		const upload:any = await s3FileUpload(file, folderPath);

		if (Object.keys(upload)?.length > 0)
		{
			setFiles((prevFiles) => ({
				...prevFiles,
				[type] : upload as string
			}));
			setLoading(false);
		}
	};
	const formErr = (file, type) => (type === 'introVideo' && file?.type !== 'video/mp4') || ((type === 'background' && !imgTypes.includes(file?.type)));

	const validateFileSize = (file, type) =>
	{
		const twoHundrenMB = 200 * 1024 * 1024; // 200 MB
		const fiftyMB = 50 * 1024 * 1024; // 50 MB

		return (type === 'introVideo' && file.size > twoHundrenMB) || (type === 'background' && file.size > fiftyMB);
	};

	const handleFileChange = (type: keyof FileInputRefs): UploadBoxProps['handleChange'] => async () =>
	{
		const input:any = fileInputRefs[type].current;

		if (input?.files?.[0])
		{
			const file = input.files[0];

			if (type === 'logo')
			{
				onSelectFile({ target: { files: [ file ] } });
			}
			else
				if (validateFileSize(file, type))
				{
					throwSizeEr(type);
				}
				else if (formErr(file, type))
				{
					throwFromErr(type);
				}
				else
				{
					uploadFile(file, type);
				}
		}
	};

	const handleDeleteFile = (type: keyof FileInputRefs) => async () =>
	{
		setFiles((prevFiles) => ({
			...prevFiles,
			[type] : null
		}));

		const input = fileInputRefs[type].current;

		await s3DeleteNew(files[type].key, '');

		if (input)
		{
			input.value = ''; // Ensure input is not null before assignment
		}
	};

	const uploadBoxes: LocalUploadBoxProps[] = [
		{
			title          : language.background,
			file           : files.background,
			fileType       : 'image',
			fileRef        : fileInputRefs.background,
			onUploadClick  : handleFileClick('background'),
			onUploadChange : handleFileChange('background'),
			onDelete       : handleDeleteFile('background'), // Pass the delete handler
			uploadText     : language.uploadWebinarBg,
			fileTypes      : `${language.fileTypes}: JPEG, JPG, PNG`,
			maxSize        : `${language?.upTo} 50 MB`,
			accept         : 'image/jpeg, image/jpg, image/png',
			editable       : true // Ensure it's editable for delete icon to show
		},
		{
			title          : language.logo,
			file           : files.logo,
			fileType       : 'image',
			fileRef        : fileInputRefs.logo,
			onUploadClick  : handleFileClick('logo'),
			onUploadChange : handleFileChange('logo'),
			onDelete       : handleDeleteFile('logo'), // Pass the delete handler
			uploadText     : language.uploadLogo,
			fileTypes      : `${language.fileTypes}: SVG, PNG, JPG`,
			accept         : 'image/jpeg, image/jpg, image/png',
			maxSize        : `${language?.upTo} 5 MB`,
			editable       : true
		},
		{
			title          : language.introvideo,
			file           : files.introVideo,
			fileType       : 'video',
			fileRef        : fileInputRefs.introVideo,
			onUploadClick  : handleFileClick('introVideo'),
			onUploadChange : handleFileChange('introVideo'),
			onDelete       : handleDeleteFile('introVideo'), // Pass the delete handler
			uploadText     : language.uploadIntroVideo,
			fileTypes      : `${language.fileTypes}: MP4`,
			accept         : 'video/mp4',
			maxSize        : `${language?.upTo} 200 MB`,
			editable       : true // Ensure it's editable for delete icon to show
		}
	];

	const handleUploadLogoClose = () =>
	{
		setInitialState({ ...initialState, showImageCropper: false });
	};

	const brandingCreate = async () =>
	{
		const { background, introVideo, logo } = files;

		try
		{
			const body:any = {
				actionType   : 'updateWebinar',
				id           : form?.id,
				webinarTheme : {
					primary : colorStates?.primaryColor,
					navBar  : colorStates?.secondaryColor
				}
			};

			if (logo)
			{
				body.webinarLogo = [
					{
						// name      : logo?.name,
						// key       : logo?.key,
						// type      : logo?.type,
						// url       : logo?.url,
						...logo,
						mediaType : 'image'
					}
				];
			}
			if (background)
			{
				body.webinarBackground = [
					{
						// name : background?.name,
						// key  : background?.key,
						// type : background?.type,
						// url  : background?.url,
						...background,
						mediaType : 'image'
					}
				];
			}
			if (introVideo)
			{
				body.webinarIntroVideo = {
					// name      : introVideo?.name,
					// key       : introVideo?.key,
					// type      : introVideo?.type,
					// url       : introVideo?.url,
					...introVideo,
					mediaType : 'mp4'
				};
			}

			setLoading(true);
			const res = await updateMeeting(body);

			if (res?.status)
			{
				setLoading(false);
				setForm({ ...form,
					webinarLogo       : body?.webinarLogo || null,
					webinarBackground : body?.webinarBackground || null,
					webinarIntroVideo : body?.webinarIntroVideo || null,
					webinarTheme      : body?.webinarTheme
				});
				handleNextCallBack();
			}
		}
		catch (error:any)
		{
			toast.error(error?.response?.data?.message, {
				position : toast.POSITION.TOP_RIGHT
			});
		}
		setLoading(false);
	};

	const btnElement = document.getElementById('webinar_preview_button');

	const togglePreview = () =>
	{
		setPreview(!preview);
	};

	return (
		<>
			<Stack direction='column' height='calc(100% - 56px)'>
				{btnElement && ReactDOM.createPortal(
					<Button variant='outlined' onClick={togglePreview}>
						{language?.preview}
					</Button>, btnElement)
				}
				{loading && <Loading/>}
				<Grid container spacing={1} px='var(--pixel16)' py='6px'>
					<Grid item xs={12} md={6}>
						<Theming setColorStates={setColorStates} colorStates={colorStates}/>
					</Grid>

					{uploadBoxes.map((box, index) => (
						<Grid item key={box?.title ?? generateRandomUUID()} pt='var(--pixel16)' xs={12} md={6}>
							<UploadBox
								{...box}
								accept={box.accept}
								handleClick={box.onUploadClick}
								handleChange={box.onUploadChange}
								handleDelete={box.onDelete}
							/>
						</Grid>
					))}
				</Grid>
			</Stack>
			<Grid
				container
				justifyContent={'space-between'}
				alignItems='center'
				p='var(--pixel16)'
			>
				<Stack
					direction='row'
					alignItems='center'
					spacing={1}
				>
					<Button
						variant='outlined'
						onClick={handleBackCallBack}
					>
						{language?.previous}
					</Button>
					<Button onClick={handleSkipFinish} variant='outlined'>
						{language?.skipandFinish}
					</Button>
				</Stack>
				<Grid item>
					<Stack direction='row' spacing={1}>
						<Button
							onClick={handleDeletePop}
							variant='outlined'
						>
							{language?.cancel}
						</Button>
						<Button
							variant='contained'
							sx={{ color: 'var(--whiteColor)' }}
							onClick={brandingCreate}
						>
							{language?.next}
						</Button>
					</Stack>
				</Grid>
			</Grid>
			{initialState?.showImageCropper && (
				<PreviewCropImg
					handleUploadLogoClose={handleUploadLogoClose}
					intialState={initialState}
					onSelectFile={onSelectFile}
					handleImageCrop={handleImageCrop}
					setCropper={setCropper}
					type='branding'
				/>
			)}
			{preview && ReactDOM.createPortal(
				<div className='flexCol mediaPreview alignCntr scrollY'>
					<BrandingPreview
						branding={files}
						theme={colorStates}
						handleClose={togglePreview}
					/>
				</div>
				, document.body)}
		</>
	);
};

export default Branding;
